import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Clock, Users, Mic, Target, Calendar, MessageSquare, Download, Loader2 } from 'lucide-react';
import { useTutorAnalytics } from '../../tutor/hooks/useTutorAnalytics';
import { getTeacherStudentsTutorSummary } from '../../tutor/api/teacherTutorAnalyticsApi';
import {
  resolveSpeakingTopicLabel,
  type TutorAnalyticsSummary,
  type DailyEngagementDoc,
  type TutorSessionDoc,
} from '../../tutor/types/tutorAnalytics';
import type { TeacherStudentSummaryItem } from '../../tutor/types/teacherTutorAnalytics';
import { TutorTrendChart } from '../../components/ai-tutor/analytics/TutorTrendChart';
import { BarChartCard } from '../../components/ai-tutor/analytics/BarChartCard';
import { PieChartCard } from '../../components/ai-tutor/analytics/PieChartCard';
import { AnalyticsReportView } from '../student/AnalyticsReportView';
import { exportElementToPdf } from '../../utils/pdfExport';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler';

export const TeacherStudentAnalyticsPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get student data from navigation state (passed from the table)
  const [studentData, setStudentData] = useState<TeacherStudentSummaryItem | null>(location.state?.student || null);

  // Fetch student data if not available in navigation state
  useEffect(() => {
    if (!studentData && studentId) {
      getTeacherStudentsTutorSummary({ days: 30 }).then((response) => {
        const foundStudent = response.summaries.find(s => s.student_id === studentId);
        if (foundStudent) {
          setStudentData(foundStudent);
        }
      }).catch((err) => {
        console.warn('Could not fetch student data:', err);
      });
    }
  }, [studentData, studentId]);

  const { data, loading, error } = useTutorAnalytics({
    studentId: studentId || '',
    initialDays: 30
  });

  const [isExporting, setIsExporting] = useState(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);

  const dailyEngagementBars = useMemo(() => {
    const d = data?.daily_engagement ?? [];
    return d
      .slice()
      .sort((a: DailyEngagementDoc, b: DailyEngagementDoc) => (a.date < b.date ? -1 : 1))
      .map((x: DailyEngagementDoc) => ({
        label: new Date(x.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        sessions: x.sessions ?? 0,
        tutor_minutes: x.tutor_minutes ?? 0,
      }));
  }, [data]);

  const speakingAverages = useMemo(() => {
    const sessions = data?.speaking_sessions ?? [];
    if (!sessions.length) return [];
    const keys = ['pronunciation', 'fluency', 'grammar', 'vocabulary', 'overall'] as const;
    return keys.map(key => {
      const sum = sessions.reduce((s, x) => s + ((x.scores as Record<string, number>)?.[key] ?? 0), 0);
      return { label: key.charAt(0).toUpperCase() + key.slice(1), value: Math.round((sum / sessions.length) * 10) / 10 };
    });
  }, [data]);

  const tutorTypeCounts = useMemo(() => {
    const hist = data?.session_history ?? [];
    const map = new Map<string, number>();
    hist.forEach((s: TutorSessionDoc) => {
      const t = s.tutor_type || 'Unknown';
      map.set(t, (map.get(t) ?? 0) + 1);
    });
    return Array.from(map.entries()).map(([label, value]) => ({ label, value }));
  }, [data]);

  useEffect(() => {
    if (!isExporting || !reportContainerRef.current) return;
    const timer = setTimeout(() => {
      if (!reportContainerRef.current) {
        setIsExporting(false);
        return;
      }
      exportElementToPdf(reportContainerRef.current, {
        filename: 'teacher-student-analytics-report',
        delayBeforeCapture: 0,
      })
        .then(() => showSuccessToast('Report downloaded'))
        .catch((err) => showErrorToast(err instanceof Error ? err : String(err)))
        .finally(() => setIsExporting(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [isExporting]);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const mapTutorType = (t?: string) => {
    if (!t) return 'Unknown';
    if (t === 'FREE') return 'AI Lesson Tutor';
    if (t === 'TOPIC') return 'Communication Tutor';
    return t;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title={`Student Analytics: ${studentData?.student_info.display_name || 'Student'}`} subtitle="Loading student data..." />
          <div className="animate-pulse space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-24"></div>
              ))}
            </div>
            <div className="bg-gray-200 dark:bg-gray-700 rounded-xl p-6 h-96"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="space-y-6">
          <PageHeader title={`Student Analytics: ${studentData?.student_info.display_name || 'Student'}`} subtitle="Student analytics data" />
          <div className="flex items-center gap-4 mb-6">
            <Button onClick={() => navigate('/teacher/tutor-analytics')} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Students
            </Button>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <p className="text-gray-600 dark:text-gray-400">
                  {error || 'No analytics data available for this student'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          title={`Student Analytics: ${studentData?.student_info.display_name || 'Student'}`}
          subtitle={`Detailed AI tutor performance for ${studentData?.student_info.display_name || 'student'}`}
        />

        {/* Back Button and Export */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Button onClick={() => navigate('/teacher/tutor-analytics')} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Students
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsExporting(true)}
            disabled={isExporting}
          >
            {isExporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {isExporting ? 'Preparing…' : 'Export report'}
          </Button>
        </div>

        {/* Student Info Card */}
        {studentData && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Student Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Name</div>
                  <div className="font-medium">{studentData.student_info.display_name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Admission No</div>
                  <div className="font-medium">{studentData.student_info.admission_no || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Grade</div>
                  <div className="font-medium">{studentData.student_info.grade || '—'}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div className="font-medium text-sm">{studentData.student_info.email || '—'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Total Minutes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.total_tutor_minutes > 0 ? `${Math.floor(data.total_tutor_minutes / 60)}h ${data.total_tutor_minutes % 60}m` : '0m'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.sessions_count || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Mic className="w-4 h-4" />
                Speaking Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.latest_speaking_level || '—'}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Avg Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data.latest_overall_score !== null ? `${data.latest_overall_score}%` : '—'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Trend Chart */}
        <TutorTrendChart trend={data.trend ?? []} />

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BarChartCard
            title="Daily engagement (sessions)"
            subtitle="Sessions per day"
            data={dailyEngagementBars}
            getLabel={d => d.label}
            getValue={d => d.sessions}
            emptyMessage="No daily engagement data"
            orientation="vertical"
          />
          <BarChartCard
            title="Daily engagement (tutor minutes)"
            subtitle="Minutes per day"
            data={dailyEngagementBars}
            getLabel={d => d.label}
            getValue={d => d.tutor_minutes}
            valueFormat={v => `${v} min`}
            emptyMessage="No daily engagement data"
          />
          <BarChartCard
            title="Speaking score breakdown"
            subtitle="Average by category"
            data={speakingAverages}
            getLabel={d => d.label}
            getValue={d => d.value}
            valueFormat={v => `${v}%`}
            emptyMessage="No speaking session data"
          />
          <PieChartCard
            title="Session tutor type"
            subtitle="From session history"
            segments={tutorTypeCounts}
            emptyMessage="No session history"
          />
        </div>

        {/* Detailed Analytics */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Session History */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Session History ({data.session_history.length} sessions)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {data.session_history.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No sessions found</p>
                ) : (
                  data.session_history.map((session) => (
                    <div key={session.session_id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              Session
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              session.mode === 'VOICE'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {session.mode}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              session.tutor_type === 'FREE'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                            }`}>
                              {mapTutorType(session.tutor_type)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {session.started_at ? formatDate(session.started_at) : 'Unknown date'} • Duration: {session.duration_seconds ? formatDuration(session.duration_seconds) : 'Unknown'}
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-gray-600 dark:text-gray-400">
                            {session.turns_user || 0} user • {session.turns_model || 0} assistant
                          </div>
                          {session.lesson_no && (
                            <div className="text-gray-600 dark:text-gray-400">
                              Lesson {session.lesson_no}
                            </div>
                          )}
                          {session.topic_label && (
                            <div className="text-gray-600 dark:text-gray-400">
                              {session.topic_label}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Speaking Sessions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5" />
                Speaking Sessions ({data.speaking_sessions.length} evaluations)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {data.speaking_sessions.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No speaking evaluations found</p>
                ) : (
                  data.speaking_sessions.map((session, idx) => (
                    <div key={session.session_id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900 dark:text-white">
                              Evaluation {idx + 1}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200`}>
                              {session.speaking_level}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {session.created_at ? formatDate(session.created_at) : 'Unknown date'} • Duration: {session.duration_seconds ? formatDuration(session.duration_seconds) : 'Unknown'}
                          </div>
                          <div className="text-sm mt-1.5 text-gray-800 dark:text-gray-200">
                            <span className="text-gray-500 dark:text-gray-400">Topic: </span>
                            <span className="font-medium">
                              {data ? resolveSpeakingTopicLabel(session, data.session_history) || '—' : '—'}
                            </span>
                            {session.tutor_type === 'TOPIC' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                                Communication
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${session.scores?.overall ? getScoreColor(session.scores.overall) : 'text-gray-500'}`}>
                            {session.scores?.overall || 0}%
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">Overall Score</div>
                        </div>
                      </div>

                      {session.scores && (
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.pronunciation)}`}>
                              {session.scores.pronunciation}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Pronunciation</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.fluency)}`}>
                              {session.scores.fluency}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Fluency</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.grammar)}`}>
                              {session.scores.grammar}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Grammar</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.vocabulary)}`}>
                              {session.scores.vocabulary}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Vocabulary</div>
                          </div>
                        </div>
                      )}

                      {session.feedback_ta && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Feedback</h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                            {session.feedback_ta}
                          </p>
                        </div>
                      )}

                      {session.recommendations && session.recommendations.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Recommendations</h4>
                          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            {session.recommendations.map((rec, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-blue-500 mt-1">•</span>
                                <span>{rec}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Daily Engagement */}
        {data.daily_engagement.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Daily Engagement ({data.daily_engagement.length} days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.daily_engagement.map((day) => (
                  <div key={day.date} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="font-medium text-gray-900 dark:text-white mb-2">
                      {new Date(day.date).toLocaleDateString(undefined, {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <div>Sessions: {day.sessions}</div>
                      <div>Minutes: {Math.floor((day.tutor_minutes || 0) / 60)}h {(day.tutor_minutes || 0) % 60}m</div>
                      <div>Last Active: {day.last_active_at ? formatDate(day.last_active_at) : 'Unknown'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {isExporting && (
          <div
            ref={reportContainerRef}
            style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1, width: 794 }}
            aria-hidden
          >
            <AnalyticsReportView
              tutorData={data}
              writingItems={[]}
              days={30}
              studentName={studentData?.student_info?.display_name}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};